
import { Component, Mixins, Prop, Vue } from "vue-property-decorator"
import { Getter, namespace, State } from "vuex-class"

import vSelect from "vue-select"

import CaseLinks from "@/components/CaseDetails/CaseLinks.vue"
import CaseUsers from "@/components/CaseDetails/CaseUsers.vue"
import Invite from "@/components/CaseDetails/Users/Invite.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import CaseFields from "./CaseFields.vue"
import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import ReopenCaseModal from "@/components/CaseDetails/ReopenCaseModal.vue"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import UserImage from "@/components/UserImage.vue"
import FormGroup from "@/components/Fields/FormGroup.vue"

import CaseFile from "@/types/caseFile"

import { Area, CaseDetails, CaseField, Severity, Status, TemplateDetails } from "@/types"
import { CaseEvent, CaseEvents } from "@/types/originatingEvents"
import { CurrentUser, UserWithFields } from "@/types/user"

import api from "@/services/api"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import { BModal } from "bootstrap-vue"

import { LayoutLocations } from "@/types/templateLayout"

import { IntegrationOutOption } from "@/types/integration"
import { FormatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { TenantFeatures } from "@/types/tenantFeatures"
import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"
import { getCanEditClosedCase, getCanReopenClosedCase, getCanUserDisableCadence, getIsCadenceVisibleOnCase, reopenCase as reopenCurrentCase } from "@/scripts/caseDetails"

const caseStore = namespace("cases")

@Component({
	components: {
		"user-image": UserImage,
		"form-group": FormGroup,
		"case-users": CaseUsers,
		"tags": Tags,
		"v-select": vSelect,
		"invite-user": Invite,
		"case-links": CaseLinks,
		"case-actions": CaseActions,
		"area-select": AreaSelect,
		"case-fields": CaseFields,
		"loader": SkeletonLoader,
		"case-merge-modal": CaseMergeModal,
		"reopen-case-modal": ReopenCaseModal
	}
})
export default class CaseSideBar extends Mixins(FormatFieldsMixin) {
	$refs!: {
		confirmCloseModal: BModal
		caseMergeModal: CaseMergeModal,
	}

	@Getter isFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];
	@caseStore.State("areas") public areas!: Area[];

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents | null;

	@caseStore.Action public caseOpen!: (caseId: string) => void;

	@State
	public me!: CurrentUser;

	@caseStore.Mutation
	public setShowCCEvent!: (ccEvent: CaseEvent) => void;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Prop({ default: false })
	canEditCurrentCase!: boolean;

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop()
	public caseFiles!: CaseFile[];

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop({ default: false })
	canMakeClosedChanges!: boolean

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

	LayoutLocations = LayoutLocations

	closeModalShown: boolean = false
	reopenModalShown: boolean = false

	copiedItems = ""

	TenantFeatures = TenantFeatures

	private caseUpdating = false
	private caseUpdateQueued = false
	private savingFields: { [key: string]: boolean } = {}

	public get anyUserRoles () {
		if (!this.currentCase || !this.currentCase.fields) return false
		return Object.keys(this.currentCase.fields)
			.some(fieldId => this.currentCase.fields[fieldId].field.dataType === "user-role")
	}

	public get isCadenceVisible (): boolean {
		return getIsCadenceVisibleOnCase(this.currentCase, this.template)
	}

	public get isDisableCadenceVisible (): boolean {
		return getCanUserDisableCadence(this.currentCase, this.template, this.me)
	}

	private async onCaseChanged () {
		if (this.caseUpdating) {
			this.caseUpdateQueued = true
			return
		}

		if (this.currentCase.id) {
			this.caseUpdating = true
			await api.saveCase(this.currentCase)
			this.caseUpdating = false

			if (this.caseUpdateQueued) {
				this.caseUpdateQueued = false
				this.onCaseChanged()
			}
		}
	}

	public openEvent (event: CaseEvent) {
		this.setShowCCEvent(event)
	}

	/**
	 * Saves the case after a built in field changes, showing the saving state on that field while it is in flight.
	 * @param fieldId - The id of the field being saved.
	 */
	public async saveBuiltInField (fieldId: string): Promise<void> {
		Vue.set(this.savingFields, fieldId, true)

		try {
			await this.onCaseChanged()
		} finally {
			Vue.delete(this.savingFields, fieldId)
		}
	}

	public get showBuiltInFields (): boolean {
		return this.showField(this.template, "status") ||
			this.showField(this.template, "severity") ||
			this.showField(this.template, "affectedAreas") ||
			this.showField(this.template, "tags") ||
			this.showField(this.template, "links")
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		return allFields.filter(field => this.fieldRequired(field))
	}

	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.caseFiles.filter(caseFile => caseFile.fieldId === id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && !isCase) || (action.requiredCase && isCase)) &&
															action.completedBy === null)
	}

	public get canClose (): boolean {
		return (!this.caseFieldsNotValidated || this.caseFieldsNotValidated.length === 0) && this.caseActionsValidated
	}

	private closeCase () {
		if (this.canClose) {
			this.closeModalShown = true
		}
	}

	private confirmClose () {
		this.$emit("closeCase")
		this.closeModalShown = false
	}

	private openReopenCaseModal () {
		this.reopenModalShown = true
	}

	private async reopenCase () {
		await reopenCurrentCase(this.currentCase)
		this.reopenModalShown = false
	}

	private cancelCloseCase () {
		this.$emit("cancelCloseCase")
	}

	get canEditClosedChanges (): boolean {
		return getCanEditClosedCase(this.template, this.me)
	}

	get templateCanReopenClosed (): boolean {
		return getCanReopenClosedCase({
			reopenClosedCasesEnabled: this.isFeatureEnabled(TenantFeatures.ReopenClosedCases),
			template: this.template,
			me: this.me
		})
	}

	get caseRecordName (): string {
		return this.currentCase.isCase
			? this.getCaseName
			: this.getRecordName
	}

	get showCloseCaseButton () {
		return this.currentCase &&
			this.currentCase.id &&
			this.canEditCurrentCase &&
			!this.currentCase.isClosed &&
			this.isFeatureEnabled(TenantFeatures.SidebarCloseButton)
	}

	get showReopenCaseButton () {
		return this.currentCase &&
			this.currentCase.id &&
			this.currentCase.isClosed &&
			this.isFeatureEnabled(TenantFeatures.SidebarCloseButton) &&
			this.templateCanReopenClosed
	}

	get originatingEvents (): CaseEvent[] {
		return this.caseEvents?.originatingEvents ?? []
	}

	get originatingEventLabel (): string {
		return `Originating Event${this.originatingEvents.length > 1 ? "s" : ""}`
	}

	public async copyCaseDetails () {
		if (this.currentCase.caseNumber && this.currentCase.title) {
			const eventNumbers = this.originatingEvents.map(event => `#${event.eventNumber}`).join(", ")
			const eventText = eventNumbers ? `\n${this.originatingEventLabel}: ${eventNumbers}` : ""
			const eventHtml = eventNumbers ? `<p>${this.originatingEventLabel}: ${eventNumbers}</p>` : ""

			const caseDetatils = `Title: ${this.currentCase.title}\n${this.getCaseName}: #${this.currentCase.caseNumber}${eventText}\nUrl: ${window.location.href}`
			const html = `<p>Title: ${this.currentCase.title}</p><p>${this.getCaseName}: #${this.currentCase.caseNumber}</p>${eventHtml}<p>Url: <a href="${window.location.href}"> ${window.location.href}</a></p>`
			const type = "text/html"
			const blob = new Blob([html], { type: "text/html" })
			const blob2 = new Blob([caseDetatils], { type: "text/plain" })
			try {
				// @ts-ignore
				await navigator.clipboard.write([new ClipboardItem({ [type]: blob, "text/plain": blob2 })])
				this.copiedItems = "Copied"
			} catch (err) {
			}
		}
	}

	get showConsolidatedInfo () {
		return this.isFeatureEnabled(TenantFeatures.ConsolidatedRecordInfo) && this.currentCase && this.currentCase.id
	}

	get getCopyTooltip () {
		return this.copiedItems
	}
}

